//REV-21372 — Vokabular der beiden Uebersichten „Kommentare" und „Pendenzen".
//
//⚠️ EINE Komponente, zweimal montiert (ADR-0019 E7) — parametrisiert ueber `kind`. Unterschiedlich
//sind nur Spaltensatz, Pillen-Satz, Leertext und Vorsortierung; die Mechanik ist dieselbe. Ein
//zweites Bauteil haette zwei Tabellen-Regler zu pflegen bedeutet, die auseinanderlaufen.
//
//⚠️ „Pendenz" heisst HIER die frei erfasste Aufgabe — nicht die aus den Rule-Packs abgeleiteten
//Zeilen des Arbeitsprogramms (REV-21759). Siehe Glossar.

import { NoteAuthor, NoteKind, NoteSource, NoteTag } from '../account-notes/account-notes.types';
import { SortDirection, SortState } from '../sort-header/sort-header.types';

export { NoteAuthor, NoteKind, NoteSource, NoteTag };

/** Pillen-Reihe der Kommentare (AC2). */
export type CommentFilterKey = 'all' | 'internal' | 'closingMeeting';

/** Pillen-Reihe 1 der Pendenzen (AC3). Es gibt kein „alle" — offen ODER erledigt. */
export type TodoStatusKey = 'open' | 'done';

/** Pillen-Reihe 2 der Pendenzen (AC3). */
export type TodoAudienceKey = 'all' | 'todo' | 'reviewer' | 'client';

/**
 * Spalten beider Uebersichten. Die beiden Saetze ueberschneiden sich absichtlich nicht vollstaendig:
 * `due` gibt es nur an der Pendenz, `tags` heisst dort Zielgruppe statt Klassifizierung.
 */
export type NoteSortKey = 'text' | 'tags' | 'account' | 'author' | 'created' | 'due';

export type NoteSortDirection = SortDirection;

export type NoteSort = SortState<NoteSortKey>;

/**
 * Eine Zeile der Uebersicht — **anzeige-fertig**.
 *
 * ⚠️ Kein Datum wird hier gerechnet und keine Zeile hier sortiert: `createdLabel`, `dueLabel` und
 * `overdue` kommen herein. Eine dumme Komponente, die „ueberfaellig" selbst ausrechnet, rechnet es
 * an drei Stellen — und rechnet es in der Zeitzone des Browsers, nicht in der des Mandats.
 */
export interface NoteRow {
    id: string;
    kind: NoteKind;
    /** Der Text selbst ist die erste Spalte — gekuerzt dargestellt, vollstaendig im Tooltip. */
    text: string;
    /** Klassifizierung (Kommentar) bzw. Zielgruppe (Pendenz). */
    tags: NoteTag[];
    source: NoteSource;
    /**
     * Kontonummer und -bezeichnung GETRENNT — wie bei den Korrekturbuchungen: die Nummer
     * monospace obendrueber, der Name gedaempft darunter. Zusammengeklebt liessen sich die Nummern
     * nicht mehr spaltenweise vergleichen, und genau dafuer liest man diese Spalte.
     *
     * `null` ⇒ die Zeile haengt an keinem Konto (z. B. eine Mandats-Pendenz).
     */
    accountNumber?: string | null;
    accountName?: string | null;
    accountId?: string | null;
    author: NoteAuthor;
    createdLabel: string;
    /** Nur bei `TODO`; `null` ⇒ ohne Faelligkeit. */
    dueLabel?: string | null;
    /**
     * Nur bei `TODO`. Kommt herein (siehe oben).
     *
     * ⚠️ Eine ERLEDIGTE Pendenz ist nie ueberfaellig (AC6) — diese Regel gehoert dem Host, damit
     * beide Uebersichten und der Export dieselbe Antwort geben.
     */
    overdue?: boolean;
    done?: boolean;
    /**
     * Only a `TODO` can be assigned. `name: null` ⇒ the user is not in the list (left the
     * organisation); the avatar then shows a neutral placeholder rather than disappearing.
     */
    assignee?: { id: string; name: string | null } | null;
}

export interface CommentCounts {
    all: number;
    internal: number;
    closingMeeting: number;
}

/**
 * Zaehlungen der Pendenzen-Pillen.
 *
 * ⚠️ Die Zielgruppen-Zahlen folgen der ERSTEN Wahl (AC3): unter „Erledigt" zaehlen sie erledigte
 * Pendenzen. Das rechnet der Host — die Ansicht zeigt die Zahl, die sie bekommt.
 */
export interface TodoCounts {
    open: number;
    done: number;
    todo: number;
    reviewer: number;
    client: number;
}

/**
 * Ein aktiver Spaltenfilter, **anzeige-fertig beschriftet** (AC4).
 *
 * Die Ansicht baut die Beschriftung nicht: „Konto: 1022" und „Von: Sarah Müller" kommen fertig
 * herein, damit der Satz uebersetzt und nicht zusammengeklebt ist.
 */
export interface ActiveFilterChip {
    key: NoteSortKey;
    label: string;
}
